import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import StructuresGrid from '../components/StructuresGrid';
import UploadPanel from '../panels/UploadPanel';

export default function CenterContent({
  // Grid props
  displayData,
  columns,
  loading,
  selectedIds,
  onSelectionChange,
  onRowClick,
  columnVisibilityModel,
  onColumnVisibilityChange,
  isPreviewMode,
  previewData,
  onCancelPreview,
  onSaveChanges,
  searchTerm,
  onSearchChange,
  searchOptions,
  
  // Upload panel props
  bottomPanelOpen,
  setBottomPanelOpen,
  bottomPanelHeight,
  setBottomPanelHeight,
  uploadedFiles,
  onFilesUploaded,
  onClearFiles,
  selectedUploadIds,
  onUploadSelectionChange,
  uploadProject,
  onProjectChange,
  uploadTarget,
  onTargetChange,
  uploadBatch,
  onBatchChange,
  uploadSuggestions,
  onSubmitUpload,
  onCancelUpload,
  isUploading,
  onFileClick,
}) {
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Main Content - dynamically sized based on upload panel */}
      <Box 
        sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          mb: bottomPanelOpen ? `${bottomPanelHeight + parseInt(theme.spacing(5))}px` : theme.spacing(5) // Dynamic margin based on upload panel
        }}
      >
        <StructuresGrid
          data={displayData}
          columns={columns}
          loading={loading}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          onRowClick={onRowClick}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityChange={onColumnVisibilityChange}
          isPreviewMode={isPreviewMode}
          previewData={previewData}
          onCancelPreview={onCancelPreview}
          onSaveChanges={onSaveChanges}
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          searchOptions={searchOptions}
        />
      </Box>

      {/* Upload Panel - positioned absolutely but pushes content up via margin */}
      {bottomPanelOpen && (
        <UploadPanel
          isOpen={bottomPanelOpen}
          onToggle={setBottomPanelOpen}
          height={bottomPanelHeight}
          onHeightChange={setBottomPanelHeight}
          uploadedFiles={uploadedFiles}
          onFilesUploaded={onFilesUploaded}
          onClearFiles={onClearFiles}
          selectedFileIds={selectedUploadIds}
          onSelectionChange={onUploadSelectionChange}
          uploadProject={uploadProject}
          onProjectChange={onProjectChange}
          uploadTarget={uploadTarget}
          onTargetChange={onTargetChange}
          uploadBatch={uploadBatch}
          onBatchChange={onBatchChange}
          suggestions={uploadSuggestions}
          onSubmit={onSubmitUpload}
          onCancel={onCancelUpload}
          isUploading={isUploading}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityChange={onColumnVisibilityChange}
          onFileClick={onFileClick}
        />
      )}

      {/* Fixed Bottom Toolbar - Always Visible */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: theme.spacing(5), // Match IconButton height exactly
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
      >
        {bottomPanelOpen && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: theme.spacing(4), // 32px at standard density
              height: theme.spacing(0.375), // 3px at standard density
              bgcolor: 'primary.main',
            }}
          />
        )}
        <Box sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Tooltip 
            title={bottomPanelOpen ? 'Hide Upload Panel' : 'Show Upload Panel'} 
            placement="top"
          >
            <IconButton
              onClick={() => setBottomPanelOpen(!bottomPanelOpen)}
              sx={{
                width: theme.spacing(5),
                height: theme.spacing(5), // Full height of toolbar
                color: bottomPanelOpen ? 'primary.main' : 'text.secondary',
                bgcolor: bottomPanelOpen ? 'action.selected' : 'transparent',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
                borderRadius: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: theme.dimensions.icon.medium,
                },
              }}
            >
              <UploadFile />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}